import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DoCheck,
    ElementRef,
    HostBinding,
    Input,
    OnDestroy,
    Optional,
    Renderer2,
    Self,
    ViewChild,
} from "@angular/core";
import { NgControl } from "@angular/forms";
import { RxEventually } from "@spscommerce/ng-utils";
import { onNextTick } from "@spscommerce/utils";

import { SpsFormControlComponent } from "../forms/sps-form-control.abstract.component";
import { SpsOptionListComponent } from "../option-list/sps-option-list.component";

@Component({
    selector: "sps-autocomplete",
    templateUrl: "./sps-autocomplete.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SpsAutocompleteComponent extends SpsFormControlComponent<string> implements DoCheck, OnDestroy {
    static readonly displayName = "sps-autocomplete";
    static readonly props = {
        suggestions: {
            type: `
                RxEventually<Iterable<string>>
                | ((filter?: string) => RxEventually<Iterable<string>>)
            `,
            required: true
        },
        placeholder: "string",
        searchDebounce: "number",
    };

    private static _instances = new Set<SpsAutocompleteComponent>();

    @HostBinding("class.sps-autocomplete--open")
    @HostBinding("class.z-stratum-dropdown")
    isOpen;

    @HostBinding("class.sps-autocomplete--opens-upward")
    opensUpward;
    get pending() {
        return Boolean(
            this.dropdown &&
                this.dropdown.options &&
                typeof this.dropdown.options === "function" &&
                this.dropdown.options["pending"]
        );
    }

    /** The list of suggestions, or a function that takes in the text entered by the user and
     * returns a list of suggestions. */
    @Input() suggestions: RxEventually<Iterable<string>> | ((filter?: string) => RxEventually<Iterable<string>>) = [];
    /** The placeholder text to be displayed in the text input when it is empty. */
    @Input() placeholder = "";
    /** The amount of time to debounce the text input before updating the suggestions, in ms. */
    @Input() searchDebounce: number;

    @HostBinding("class.sps-autocomplete") readonly _spsAutocompleteClass = true;

    @ViewChild(SpsOptionListComponent, { static: false }) dropdown: SpsOptionListComponent<string>;
    /** Ref to the text input element. */
    @ViewChild("textInput", { static: false }) inputElement: ElementRef;

    get value(): string {
        return this.model || "";
    }
    set value(newValue: string) {
        this.model = newValue;
    }


    static _onDocumentClick(event: MouseEvent) {
        for (const instance of SpsAutocompleteComponent._instances) {
            if (!event || !instance._el.nativeElement.contains(event.target)) {
                instance.dropdown.close();
            }
        }
    }

    constructor(
        _el: ElementRef,
        _renderer: Renderer2,
        @Self()
        @Optional()
        _ngControl: NgControl,
        protected _changeDetector: ChangeDetectorRef
    ) {
        super(_el, _renderer, _ngControl);

        if (SpsAutocompleteComponent._instances.size === 0) {
            document.addEventListener("click", SpsAutocompleteComponent._onDocumentClick);
        }

        SpsAutocompleteComponent._instances.add(this);
    }

    ngDoCheck() {
        this.isOpen = this.dropdown && this.dropdown.isOpen && !this.dropdown.isEmpty;
        this.opensUpward = this.dropdown && this.dropdown.opensUpward;
    }

    ngOnDestroy() {
        if (SpsAutocompleteComponent._instances.has(this)) {
            SpsAutocompleteComponent._instances.delete(this);

            if (SpsAutocompleteComponent._instances.size === 0) {
                document.removeEventListener("click", SpsAutocompleteComponent._onDocumentClick);
            }
        }
    }

    selectSuggestion(suggestion: string) {
        this.value = suggestion;
        this.inputElement.nativeElement.focus();
        this.dropdown.close();
    }

    _onClearClick() {
        this.value = "";
        onNextTick(() => this.inputElement.nativeElement.focus());
    }

    setDisabledState(isDisabled: boolean): void {
        super.setDisabledState(isDisabled);
        if (this.isDisabled) {
            this.dropdown.close();
        }
        this._changeDetector.markForCheck();
    }
}
